import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  Clock,
  Sparkles,
  ArrowRight,
  ArrowUpRight,
  Users,
  Filter,
  BookOpen,
  Award,
  Star,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import TrainersSection from "@/components/TrainersSection";
import { KPI_ICON_MAP, KpiCountUp } from "@/pages/Home";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Programs() {
  // Fetch, filter, and search logic below is unchanged from before — same endpoint, same
  // state shape, same derivations. Only the rendering changed.
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [pageCms, setPageCms] = useState(null);

  useEffect(() => {
    api
      .get("/programs")
      .then((r) => setPrograms(r.data || []))
      .catch(() => setPrograms([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get("/cms/programs").then((r) => setPageCms(r.data)).catch(() => setPageCms({}));
  }, []);

  // KPI cards — CMS-driven, visible-only, sorted by order. No hardcoded values;
  // if nothing is configured/visible, the section renders nothing (see below).
  const kpiStats = useMemo(() => {
    const stats = Array.isArray(pageCms?.stats) ? pageCms.stats : [];
    return stats
      .filter((s) => s.visible !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [pageCms]);

  const cats = useMemo(
    () => ["All", ...Array.from(new Set(programs.map((p) => p.category).filter(Boolean)))],
    [programs]
  );

  const filtered = useMemo(
    () =>
      programs.filter(
        (p) =>
          (cat === "All" || p.category === cat) &&
          (!q ||
            (p.title || "").toLowerCase().includes(q.toLowerCase()) ||
            (p.subtitle || "").toLowerCase().includes(q.toLowerCase()) ||
            (p.category || "").toLowerCase().includes(q.toLowerCase()))
      ),
    [programs, q, cat]
  );

  // Uses the real `featured` flag on the Program model (added in an earlier redesign
  // round) instead of the previous behavior, which just took the first search result and
  // labeled it "Featured" regardless of whether it actually was.
  const featured = useMemo(() => filtered.find((p) => p.featured) || filtered[0], [filtered]);
  const rest = useMemo(() => filtered.filter((p) => p.id !== featured?.id), [filtered, featured]);

  return (
    <div data-testid="programs-page" style={{ backgroundColor: "#F8FAFC" }}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,104,231,0.08),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(69,184,172,0.08),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-16 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <motion.div
                variants={fadeUp} initial="hidden" animate="show" custom={0}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6"
                style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}
              >
                <BookOpen className="w-3.5 h-3.5" aria-hidden="true" /> Training programs
              </motion.div>
              <motion.h1
                variants={fadeUp} initial="hidden" animate="show" custom={1}
                className="rebrand-serif tracking-tight font-bold leading-[1.05]"
                style={{ fontSize: "clamp(2.5rem, 5vw + 1rem, 4rem)", color: "#1A202C" }}
                data-testid="programs-headline"
              >
                Professional <span style={{ color: "#3B68E7" }}>teacher training</span> that lasts.
              </motion.h1>
              <motion.p
                variants={fadeUp} initial="hidden" animate="show" custom={2}
                className="mt-6 text-base sm:text-lg leading-relaxed max-w-2xl"
                style={{ color: "#64748B" }}
              >
                Modular, research-backed programs delivered on-site or online. Every program includes assessments,
                artefacts, and QR-verified certificates.
              </motion.p>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/book" data-testid="programs-cta-book"
                  className="group inline-flex items-center gap-2 text-white text-sm font-semibold transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px", boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}
                >
                  Book a training <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                </Link>
                <a
                  href="#catalog"
                  className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
                  style={{ border: "2px solid #DDA756", color: "#DDA756", borderRadius: 50, padding: "10px 22px" }}
                >
                  Browse catalog
                </a>
              </motion.div>
            </div>

            {kpiStats.length > 0 && (
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="lg:col-span-4">
                <style>{`
                  #programs-kpi-grid { grid-template-columns: repeat(${pageCms?.kpi_columns_mobile ?? 2}, 1fr); }
                  @media (min-width: 640px) and (max-width: 1023px) { #programs-kpi-grid { grid-template-columns: repeat(${pageCms?.kpi_columns_tablet ?? 2}, 1fr); } }
                  @media (min-width: 1024px) { #programs-kpi-grid { grid-template-columns: repeat(${pageCms?.kpi_columns_desktop ?? 4}, 1fr); } }
                `}</style>
                <div
                  id="programs-kpi-grid"
                  className="grid overflow-hidden"
                  style={{
                    gap: pageCms?.kpi_gap ?? 1,
                    borderRadius: 20,
                    backgroundColor: "#E2E8F0",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  {kpiStats.map((kpi, i) => {
                    const KpiIcon = KPI_ICON_MAP[kpi.icon] || Star;
                    const hasIcon = !!kpi.icon;
                    const align = pageCms?.kpi_alignment === "center" ? "center" : "left";
                    return (
                      <div
                        key={kpi.id || i}
                        className="bg-white"
                        style={{
                          paddingLeft: pageCms?.kpi_card_padding_x ?? 20,
                          paddingRight: pageCms?.kpi_card_padding_x ?? 20,
                          paddingTop: pageCms?.kpi_card_padding_y ?? 24,
                          paddingBottom: pageCms?.kpi_card_padding_y ?? 24,
                          textAlign: align,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: align === "center" ? "center" : "flex-start",
                        }}
                      >
                        {hasIcon && (
                          <div
                            className="mb-2 inline-flex items-center justify-center shrink-0"
                            style={{
                              width: kpi.iconContainerSize ?? 28,
                              height: kpi.iconContainerSize ?? 28,
                              borderRadius: kpi.iconBorderRadius ?? 8,
                              background: kpi.iconBg || "rgba(26,32,44,0.06)",
                            }}
                          >
                            <KpiIcon style={{ width: kpi.iconSize ?? 14, height: kpi.iconSize ?? 14, color: kpi.iconColor || "#1A202C" }} />
                          </div>
                        )}
                        <div
                          className="rebrand-serif text-2xl sm:text-3xl font-bold tracking-tight"
                          style={{ color: kpi.numberColor || "#1A202C", fontSize: kpi.numberFontSize ? `${kpi.numberFontSize}px` : undefined, fontWeight: kpi.numberFontWeight ?? 700 }}
                        >
                          <KpiCountUp kpi={kpi} />
                        </div>
                        <div className="mt-1 text-[11px] sm:text-xs" style={{ color: kpi.labelColor || "#64748B" }}>
                          {kpi.label || kpi.v}
                        </div>
                        {kpi.description && (
                          <div className="mt-0.5 text-[10px]" style={{ color: kpi.descriptionColor || "#94A3B8" }}>
                            {kpi.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Search + filters */}
      <section id="catalog" className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl" style={{ borderBottom: "1px solid #E2E8F0" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search programs, categories…"
              data-testid="programs-search"
              className="w-full pl-10 pr-3.5 py-2.5 outline-none text-sm bg-white transition-all"
              style={{ borderRadius: 50, border: "1px solid #E2E8F0" }}
              onFocus={(e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; }}
              onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }}
            />
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs font-medium mr-1" style={{ color: "#64748B" }}>
            <Filter className="w-3.5 h-3.5" aria-hidden="true" /> Category
          </div>
          <div className="flex flex-wrap gap-2 overflow-x-auto no-scrollbar">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`filter-${c}`}
                className="whitespace-nowrap text-sm px-3.5 py-1.5 transition-colors"
                style={{
                  borderRadius: 50,
                  border: `1px solid ${cat === c ? "#1A202C" : "#E2E8F0"}`,
                  backgroundColor: cat === c ? "#1A202C" : "#FFFFFF",
                  color: cat === c ? "#FFFFFF" : "#374151",
                }}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="ml-auto text-xs font-medium" style={{ color: "#64748B" }}>
            {filtered.length} {filtered.length === 1 ? "program" : "programs"}
          </div>
        </div>
      </section>

      {/* Featured masterclass — driven by the real featured flag, not "whatever matched first" */}
      {featured && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-16">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#3B68E7" }}>Featured masterclass</div>
              <h2 className="rebrand-serif text-2xl sm:text-4xl font-bold tracking-tight" style={{ color: "#1A202C" }}>This month&apos;s spotlight.</h2>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="grid lg:grid-cols-[1.3fr,1fr] gap-0 overflow-hidden bg-white transition-shadow duration-300 hover:shadow-[0_20px_45px_rgba(0,0,0,0.08)]"
            style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
            data-testid="programs-featured"
          >
            <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[420px] overflow-hidden bg-gray-100 group">
              <img
                src={featured.cover_image} alt={featured.title}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
              />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[10px] font-bold uppercase tracking-wider" style={{ color: "#3B68E7" }}>
                <Sparkles className="w-3 h-3" aria-hidden="true" /> Featured
              </div>
            </div>
            <div className="p-8 sm:p-10 flex flex-col">
              {featured.category && (
                <div className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: "#3B68E7" }}>{featured.category}</div>
              )}
              <h3 className="rebrand-serif text-2xl sm:text-3xl font-bold leading-tight" style={{ color: "#1A202C" }}>{featured.title}</h3>
              {featured.subtitle && <p className="mt-4 leading-relaxed" style={{ color: "#64748B" }}>{featured.subtitle}</p>}
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" style={{ color: "#64748B" }}>
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" aria-hidden="true" /> {featured.duration_hours || 0}h</span>
                <span className="inline-flex items-center gap-1.5 capitalize"><Award className="w-3.5 h-3.5" aria-hidden="true" /> {featured.level || "All levels"}</span>
              </div>

              <div className="mt-auto pt-8 flex flex-wrap items-center gap-3">
                <Link
                  to={`/programs/${featured.id}`} data-testid={`programs-featured-view-${featured.id}`}
                  className="group inline-flex items-center gap-2 text-white text-sm font-semibold transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px", boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}
                >
                  View program <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                </Link>
                <Link
                  to="/book"
                  className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
                  style={{ border: "1px solid #E2E8F0", color: "#1A202C", borderRadius: 50, padding: "11px 22px" }}
                >
                  Book now
                </Link>
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* Full catalog grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#3B68E7" }}>Full catalog</div>
            <h2 className="rebrand-serif text-2xl sm:text-4xl font-bold tracking-tight" style={{ color: "#1A202C" }}>All programs.</h2>
          </div>
          <div className="text-xs flex items-center gap-1.5" style={{ color: "#64748B" }}>
            <Users className="w-3.5 h-3.5" aria-hidden="true" /> Delivered on-site or online
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="overflow-hidden bg-white animate-pulse" style={{ borderRadius: 24, border: "1px solid #E2E8F0" }}>
                <div className="aspect-[16/10] bg-gray-100" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-20 bg-gray-100 rounded" />
                  <div className="h-5 w-3/4 bg-gray-100 rounded" />
                  <div className="h-3 w-full bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 sm:p-16 text-center" style={{ borderRadius: 24, border: "2px dashed #E2E8F0" }}>
            <Search className="w-8 h-8 mx-auto mb-3" style={{ color: "#CBD5E1" }} aria-hidden="true" />
            <div className="rebrand-serif text-xl font-semibold" style={{ color: "#1A202C" }}>No programs match your search.</div>
            <p className="mt-2 text-sm" style={{ color: "#64748B" }}>Try clearing filters or searching a different keyword.</p>
            <button
              onClick={() => { setQ(""); setCat("All"); }}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
              style={{ color: "#3B68E7" }}
            >
              Reset filters
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <motion.div key={p.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}>
                <Link
                  to={`/programs/${p.id}`} data-testid={`program-card-${p.id}`}
                  className="group block overflow-hidden bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_14px_35px_rgba(0,0,0,0.08)]"
                  style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-gray-100 relative">
                    <img
                      src={p.cover_image} alt={p.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                    />
                    {p.category && (
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] font-bold uppercase tracking-wider" style={{ color: "#3B68E7" }}>
                        {p.category}
                      </div>
                    )}
                    {p.featured && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full backdrop-blur text-[10px] font-semibold text-white" style={{ backgroundColor: "#1A202CE6" }}>
                        <Sparkles className="w-3 h-3" aria-hidden="true" /> Featured
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="rebrand-serif text-lg font-semibold leading-snug line-clamp-2" style={{ color: "#1A202C" }}>{p.title}</h3>
                    {p.subtitle && <p className="mt-2 text-sm line-clamp-2 leading-relaxed" style={{ color: "#64748B" }}>{p.subtitle}</p>}
                    <div className="mt-5 pt-4 flex items-center justify-between text-xs" style={{ borderTop: "1px solid #F1F5F9" }}>
                      <span className="flex items-center gap-3" style={{ color: "#64748B" }}>
                        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" /> {p.duration_hours || 0}h</span>
                        <span style={{ color: "#CBD5E1" }}>·</span>
                        <span className="capitalize">{p.level || "All levels"}</span>
                      </span>
                      <span className="font-medium inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-all" style={{ color: "#3B68E7" }}>
                        View <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Trainers — shared component, unchanged */}
      <TrainersSection
        eyebrow="Instructors"
        title="Learn from practitioners."
        description="Every program is delivered by trainers with classroom experience and research credentials."
        variant="compact"
        limit={6}
        columns="md:grid-cols-2 lg:grid-cols-3"
        testid="programs-trainers-section"
      />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 pb-24">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="relative overflow-hidden p-10 sm:p-16"
          style={{ borderRadius: 32, background: "linear-gradient(135deg, #3B68E7 0%, #2947B0 100%)" }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.12),transparent_55%)]" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center text-white">
            <div>
              <Sparkles className="w-6 h-6 mb-4" style={{ color: "#DDA756" }} aria-hidden="true" />
              <h2 className="rebrand-serif text-3xl sm:text-5xl tracking-tight font-bold">Can&apos;t find the right fit?</h2>
              <p className="mt-4 text-white/80 max-w-md">
                We design custom training arcs for schools and districts. Tell us your goals — we&apos;ll build the program.
              </p>
            </div>
            <div className="md:justify-self-end flex flex-wrap gap-3">
              <Link
                to="/contact" data-testid="programs-cta-custom"
                className="bg-white hover:bg-white/90 text-sm font-semibold px-5 py-3 rounded-full inline-flex items-center gap-2 group transition-transform hover:scale-[1.02]"
                style={{ color: "#3B68E7" }}
              >
                Design a custom program <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
              </Link>
              <Link to="/book" className="border border-white/30 text-white hover:bg-white/10 text-sm font-medium px-5 py-3 rounded-full transition-colors">
                Book existing training
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
